import { useRoute } from 'vue-router';
import PtMessage from '@ui/message';
import store from '@store';
import md5 from 'js-md5';
import accountApis from '@apis/config/graphql/account';
import { awaitWrap } from '@utils/common.util';
import { resetRouter } from '@router/router';
import { useI18n } from 'vue-i18n';
import i18n from '@/assets/i18n';
import { clearLocalUserInfo, clearPtiTimeInfo } from '@master/utils/account.util';

import { Response } from '@master/utils/account.util';

type LogoutType =
    | 'ip_no_access'
    | 'error_token'
    | 'accountInfo_failed'
    | 'no_token'
    | 'user_logout'
    | 'password_change';

// 登录来源字段
export const loginFromLocalStorageKey = 'loginFrom';
export interface Variables {
    where: {
        email: string;
        adminEmail?: string;
        password: string;
        beFromGoogle?: boolean;
        beFromShopify?: boolean;
        token?: string;
    };
    data?: {
        remeberMe: boolean;
        routeQuery: object;
    };
}

export function useLogin() {
    const route = useRoute();
    const { t } = useI18n();
    return {
        t,
        login: async ({
            email,
            password,
            rememberMe,
            beFromGoogle = false,
            beFromShopify = false,
            googleUserToken = ''
        }: {
            email: string;
            password: string;
            rememberMe: boolean;
            beFromGoogle?: boolean;
            beFromShopify?: boolean;
            googleUserToken?: string;
        }) => {
            return await accountApis.login<Response, Variables>({
                where: {
                    email,
                    password: md5(password),
                    beFromGoogle,
                    beFromShopify,
                    token: googleUserToken
                },
                data: { remeberMe: rememberMe, routeQuery: route.query }
            });
        },

        superLogin: async ({
            email,
            adminEmail,
            password,
            rememberMe
        }: {
            email: string;
            adminEmail: string;
            password: string;
            rememberMe: boolean;
        }) => {
            return await accountApis.superLogin<Response, Variables>({
                where: { email, adminEmail, password: md5(password) }
            });
        },

        verifyLoginCode: async (code: string) => {
            const [err, response] = await awaitWrap(
                accountApis.verifyLoginCode({
                    code
                })
            );
            return err ? Promise.reject(err) : Promise.resolve(response);
        },
        logout
    };
}

export async function activeAccount(email: string) {
    const [err, response] = await awaitWrap(
        accountApis.sendActiveEmail({
            where: {
                email
            }
        })
    );
    return err ? Promise.reject(err) : Promise.resolve(response);
}

/**
 * logout
 */
export const logout = (type: LogoutType, source: string) => {
    store.commit('master/main/SET_ACCOUNT_INFO', null);

    if (type === 'error_token') {
        PtMessage.info(i18n.global.t('login.session_has_expired'));
    }
    if (import.meta.env.MODE !== 'production' && type === 'accountInfo_failed') {
        PtMessage.warning('获取账号信息失败，请重新登录。');
    }
    clearLocalUserInfo('logout');
    clearPtiTimeInfo(); // 清除pti时间缓存
    if (localStorage.getItem('ptDebug')) {
        alert(`logout source: ${source}`);
    }

    // Avoid cookie not cleared
    setTimeout(resetRouter, 100);
    setTimeout(() => {
        location.href = import.meta.env.VITE_BASE_URL + 'login';
    }, 333);
};

/**
 * 存储登录标识至 sessionStorage
 * 由于google登录会存在重定向问题，需将登录标识存储至sessionStorage
 * 1. widget中登录的，则需要自动关闭弹框
 * 2. 弹出热图中登录的，则需要自动关闭弹框
 */
export function setLoginFromFlag(type: string): void {
    sessionStorage.setItem(loginFromLocalStorageKey, type);
}

export function clearLoginFromFlag(): void {
    sessionStorage.removeItem(loginFromLocalStorageKey);
}

export function isLoginFromWidget(): boolean {
    return (
        location.href.indexOf('login?from=widget') !== -1 ||
        (sessionStorage.getItem(loginFromLocalStorageKey) === 'widget' && !!window.opener)
    );
}

export function hideWidget(): void {
    document.body.classList.add('hide-widget');
}

/**
 * 推送登录信息
 * 1. 弹出热图内登录弹框场景
 */
export function sendUserInfoMessage(): void {
    const userInfo = navigator.cookieEnabled && localStorage.getItem('userInfo');
    sendMessage({
        command: 'PtengineAppAuthPageLogin',
        message: {
            userInfo: userInfo ? JSON.parse(userInfo) : ''
        }
    });
}

export function sendClosePopupMessage(): void {
    sendMessage({
        command: 'PtengineAppAuthPopupClose'
    });
}

/**
 * 是否来自弹出热图授权弹框
 */
export function isLoginFromHeatmap(): boolean {
    return (
        location.href.indexOf('login?from=heatmap') !== -1 ||
        (sessionStorage.getItem(loginFromLocalStorageKey) === 'heatmap' && !!window.opener)
    );
}

function sendMessage(message: { [key: string]: any }, origin = '*'): void {
    if (window.opener?.postMessage && window.opener !== window) {
        window.opener.postMessage(message, origin);
    }
}
